import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CreditCard, Loader2, RefreshCcw, ShieldCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { UsageLimitBar } from "@/components/billing/UsageLimitBar";
import { db, formatDate, money, type SubscriptionRecord, type UsageRecord } from "@/lib/billing";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { useCompanyUsage } from "@/hooks/useCompanyUsage";
import type { ServiceRecord } from "@/lib/packageAccess";

async function fetchSubscription(userId: string, companyId: string | null): Promise<SubscriptionRecord | null> {
  const activeQuery = db
    .from("user_subscriptions")
    .select("*, packages(*)")
    .in("status", ["active", "trialing"])
    .order("created_at", { ascending: false })
    .limit(1);
  const { data, error } = await (companyId ? activeQuery.eq("company_id", companyId) : activeQuery.eq("user_id", userId)).maybeSingle();
  if (error) throw error;
  if (data) return data;

  const fallbackQuery = db
    .from("user_subscriptions")
    .select("*, packages(*)")
    .order("created_at", { ascending: false })
    .limit(1);
  const fallback = await (companyId ? fallbackQuery.eq("company_id", companyId) : fallbackQuery.eq("user_id", userId)).maybeSingle();
  if (fallback.error) throw fallback.error;
  return fallback.data;
}

async function fetchLegacyUsage(userId: string): Promise<UsageRecord | null> {
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await db
    .from("usage_counters")
    .select("*")
    .eq("user_id", userId)
    .lte("period_start", today)
    .gte("period_end", today)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function fetchPackageServices(packageId?: string | null): Promise<ServiceRecord[]> {
  if (!packageId) return [];
  const { data, error } = await db
    .from("package_services")
    .select("service_definitions(*)")
    .eq("package_id", packageId)
    .eq("included", true);
  if (error) throw error;
  return (data ?? []).map((row: any) => row.service_definitions).filter(Boolean);
}

async function functionErrorMessage(error: any) {
  const context = error?.context;
  if (context instanceof Response) {
    const body = await context.clone().json().catch(() => null);
    if (body?.error && body?.details) return `${body.error}: ${body.details}`;
    if (body?.error) return body.error as string;
  }
  return error?.message ?? "Stripe payment could not be confirmed automatically";
}

export default function BillingSubscriptionPage() {
  const { user } = useAuth();
  const { currentCompanyId, role, isPlatformOwner } = useCompany();
  const { usage: companyUsage } = useCompanyUsage();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const confirmAttemptedRef = useRef(false);
  const { data: subscription, isLoading } = useQuery({
    queryKey: ["my-subscription", user?.id, currentCompanyId],
    queryFn: () => fetchSubscription(user!.id, currentCompanyId),
    enabled: !!user,
  });
  const { data: legacyUsage } = useQuery({
    queryKey: ["my-legacy-usage", user?.id, currentCompanyId],
    queryFn: () => fetchLegacyUsage(user!.id),
    enabled: !!user && !currentCompanyId,
  });
  const { data: services = [] } = useQuery({
    queryKey: ["my-package-services", subscription?.package_id],
    queryFn: () => fetchPackageServices(subscription?.package_id),
    enabled: !!subscription?.package_id,
  });
  const confirmCheckout = useMutation({
    mutationFn: async () => {
      const sessionId = params.get("session_id");
      const { data, error } = await db.functions.invoke("confirm-checkout-session", {
        body: sessionId ? { session_id: sessionId } : {},
      });
      if (error) throw new Error(await functionErrorMessage(error));
      if (data?.synced === false) {
        throw new Error(data.details ? `${data.reason}: ${data.details}` : data.reason);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-subscription", user?.id, currentCompanyId] });
      queryClient.invalidateQueries({ queryKey: ["package-access", user?.id] });
    },
  });

  const pkg = subscription?.packages;
  const shouldConfirmCheckout = Boolean(user && !isLoading && (!subscription || subscription.status === "incomplete"));
  const canViewBilling = isPlatformOwner || role === "owner" || role === "admin";

  useEffect(() => {
    if (!shouldConfirmCheckout || confirmAttemptedRef.current || confirmCheckout.isPending || confirmCheckout.isSuccess) return;
    confirmAttemptedRef.current = true;
    confirmCheckout.mutate();
  }, [shouldConfirmCheckout, confirmCheckout]);

  if (!canViewBilling) {
    return (
      <div className="min-h-screen bg-background">
        <main className="p-6">
          <Card>
            <CardContent className="flex items-center gap-3 p-6">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Billing is available only to company owners and admins.
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const currentUsage = currentCompanyId ? {
    articles_created: companyUsage.articles_created_this_month,
    articles_published: 0,
    websites_created: companyUsage.websites_created,
    ai_credits_used: companyUsage.ai_credits_used,
    media_assets_used: companyUsage.media_assets_used,
    team_members_used: companyUsage.team_members_used,
    storage_mb_used: 0,
    bandwidth_mb_used: 0,
  } : legacyUsage ?? {
    articles_created: 0,
    articles_published: 0,
    websites_created: 0,
    ai_credits_used: 0,
    media_assets_used: 0,
    team_members_used: 0,
    storage_mb_used: 0,
    bandwidth_mb_used: 0,
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Subscription</h1>
          <p className="text-xs text-muted-foreground">Your Delapresse package, renewal, and usage limits.</p>
        </div>
        <Button asChild variant="outline" size="sm"><Link to="/billing/plans"><RefreshCcw className="mr-2 h-4 w-4" />Upgrade</Link></Button>
      </header>

      <main className="grid gap-6 p-6 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : confirmCheckout.isPending ? (
          <Card className="col-span-full">
            <CardContent className="flex items-center gap-3 p-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              Confirming your Stripe payment and activating your Delapresse package...
            </CardContent>
          </Card>
        ) : confirmCheckout.isError ? (
          <Card className="col-span-full">
            <CardContent className="flex items-center gap-3 p-6">
              <AlertTriangle className="h-5 w-5 text-warning" />
              {confirmCheckout.error?.message ?? "Stripe payment could not be confirmed automatically. Refresh this page or contact support with your checkout confirmation."}
              <Button
                size="sm"
                className="ml-auto"
                onClick={() => {
                  confirmAttemptedRef.current = false;
                  confirmCheckout.reset();
                }}
              >
                Retry activation
              </Button>
            </CardContent>
          </Card>
        ) : !subscription || !pkg ? (
          <Card className="col-span-full">
            <CardContent className="flex items-center gap-3 p-6">
              <AlertTriangle className="h-5 w-5 text-warning" />
              No active Delapresse package was found. Choose a plan to continue.
              <Button asChild size="sm" className="ml-auto"><Link to="/billing/plans">View plans</Link></Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3">
                  {pkg.name}
                  <StatusBadge status={subscription.status} />
                </CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-border p-4">
                  <p className="text-2xl font-bold">{pkg.is_enterprise ? "Custom" : money(pkg.monthly_price, pkg.currency)}</p>
                  <p className="text-xs text-muted-foreground">Renews {formatDate(subscription.current_period_end ?? subscription.trial_end)}</p>
                </div>
                <Button asChild className="w-full"><Link to="/billing/invoices"><CreditCard className="mr-2 h-4 w-4" />View invoices</Link></Button>
                {pkg.is_trial && (
                  <div className="rounded-md bg-warning/10 p-3 text-sm text-warning">
                    Trial limits are intentionally restricted. Live publishing, production site/Plesk deployment, automation, and custom domains are disabled.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Usage This Period</CardTitle>
                <CardDescription>Server-side package gates use these counters and package limits.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-5 md:grid-cols-2">
                <UsageLimitBar label="Websites" used={currentUsage.websites_created} limit={pkg.max_websites} />
                <UsageLimitBar label="Articles" used={currentUsage.articles_created} limit={pkg.max_articles_per_month} />
                <UsageLimitBar label="AI credits" used={currentUsage.ai_credits_used} limit={pkg.max_ai_credits} />
                <UsageLimitBar label="Media assets" used={currentUsage.media_assets_used} limit={pkg.max_media_assets} />
                <UsageLimitBar label="Team members" used={currentUsage.team_members_used} limit={pkg.max_team_members} />
                <UsageLimitBar label="Storage MB" used={currentUsage.storage_mb_used} limit={pkg.storage_mb} />
              </CardContent>
            </Card>
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle>Services inclus</CardTitle>
                <CardDescription>Les menus et actions serveur suivent ces droits de package.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {services.map((service) => (
                  <div key={service.id} className="rounded-md border border-border p-3">
                    <div className="flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4 text-success" />{service.name}</div>
                    <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">{service.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
